// src/pages/Reports/ContractReport.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Printer, ArrowLeft } from "lucide-react";
import "./ContractReport.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ContractReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [company, setCompany] = useState(null);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    fetchContractData();
    fetchCompanyData();
  }, [id]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/uploads")) return `${url}${imagePath}`;
    return `${url}/uploads/${imagePath}`;
  };

  const fetchContractData = async () => {
    try {
      const response = await axios.get(`${url}/api/contract/${id}`);
      console.log("Contract data:", response.data.data);
      const contractData = response.data.data;
      setContract(contractData);
      
      // Fetch the customer's photo from Documents
      if (contractData.cust_id?._id) {
        await fetchCustomerPhoto(contractData.cust_id._id);
      }
    } catch (error) {
      toast.error("Failed to load contract details");
      navigate("/ViewContract");
    }
  };

  const fetchCustomerPhoto = async (customerId) => {
    try {
      const response = await axios.get(`${url}/api/documents/customer/${customerId}`);
      const documents = response.data.data || [];
      if (documents.length > 0) {
        const docWithPhoto = documents.find(doc => doc.photos);
        if (docWithPhoto && docWithPhoto.photos) {
          setCustomerPhoto(docWithPhoto.photos);
          console.log("Customer photo found:", docWithPhoto.photos);
        } else {
          console.log("No photo found for customer");
        }
      }
    } catch (error) {
      console.error("Failed to fetch customer photo:", error);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`${url}/api/company/latest`);
      setCompany(response.data.data);
    } catch (error) {
      console.error("Failed to fetch company:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getJobNumber = () => {
    if (!contract) return "N/A";
    return contract.job_id?.no || "N/A";
  };

  const getJobTitle = () => {
    if (!contract) return "N/A";
    return contract.job_id?.category_name || "N/A";
  };

  const getJobDescription = () => {
    if (!contract) return "N/A";
    return contract.job_id?.description || "N/A";
  };

  const getShift = () => {
    if (!contract) return "N/A";
    return contract.section || "N/A";
  };

  const getPhotoUrl = () => {
    if (!customerPhoto) return null;
    return getFullImageUrl(customerPhoto);
  };

  const getCompanyLogoUrl = () => {
    if (!company?.companylogo) return null;
    return getFullImageUrl(company.companylogo);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    const printContent = generatePrintContent();
    openPrintWindow(printContent);
  };

  const generatePrintContent = () => {
    if (!contract) return "";

    const photoUrl = getPhotoUrl();
    const companyLogoUrl = getCompanyLogoUrl();
    const jobNo = getJobNumber();
    const jobTitle = getJobTitle();
    const jobDescription = getJobDescription();
    const shift = getShift();

    const reportContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Contract Report - ${contract.no}</title>
          <style>
            ${getReportStyles()}
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="report-header">
              <div class="logo-left">
                ${companyLogoUrl ? 
                  `<img src="${companyLogoUrl}" alt="Company Logo" class="company-logo" />` : 
                  '<div class="logo-placeholder">SOMIS</div>'
                }
              </div>
              <div class="company-title">
                <h1>SOMIS COMPANY</h1>
                <p class="acronym">SOMIS</p>
                <p class="location">${company?.companylocation || "Mogadishu, Somalia"}</p>
              </div>
              <div class="logo-right">
                ${companyLogoUrl ? 
                  `<img src="${companyLogoUrl}" alt="Company Logo" class="company-logo" />` : 
                  '<div class="logo-placeholder">SOMIS</div>'
                }
              </div>
            </div>

            <div class="section">
              <h2>Job Details</h2>
              <table class="data-table">
                <thead> <th>Job No</th><th>Job Title</th><th>Job Description</th> </thead>
                <tbody>
                  <tr>
                    <td>${jobNo}</td>
                    <td>${jobTitle}</td>
                    <td>${jobDescription}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Personal Detail</h2>
              <table class="data-table">
                <thead> <th>ID No</th><th>Full name</th><th>Photos</th> </thead>
                <tbody>
                  <tr>
                    <td>${contract.cust_id?.customer_id_no || contract.no || 'N/A'}</td>
                    <td>${contract.cust_id?.fullname || 'N/A'}</td>
                    <td class="photo-cell">
                      ${photoUrl ? 
                        `<img src="${photoUrl}" alt="Customer Photo" class="customer-photo" />` : 
                        '<span class="no-photo">No Image</span>'
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Client Contract</h2>
              <table class="data-table">
                <thead> <th>No</th><th>Client</th><th>Shift</th> </thead>
                <tbody>
                  <tr>
                    <td>${contract.no || 'N/A'}</td>
                    <td>${contract.client_id?.name || 'N/A'}</td>
                    <td>${shift}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Contract Start and End</h2>
              <table class="data-table">
                <thead> <th>No</th><th>Start</th><th>End</th> </thead>
                <tbody>
                  <tr>
                    <td>${contract.no || 'N/A'}</td>
                    <td>${formatDate(contract.contract_start)}</td>
                    <td>${formatDate(contract.contract_end)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Manager / Customer</h2>
              <table class="data-table">
                <thead> <th>No</th><th>Name</th><th>Signature</th> </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>${contract.cust_id?.fullname || 'N/A'}</td>
                    <td class="signature-cell">___________________________</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>${contract.client_id?.manager_name || 'N/A'}</td>
                    <td class="signature-cell">___________________________</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="footer-message">
              <p>Good luck</p>
            </div>
          </div>
        </body>
      </html>
    `;

    return reportContent;
  };

  const openPrintWindow = (content) => {
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      alert("Please allow popups for printing");
      setIsPrinting(false);
      return;
    }

    printWindow.document.write(content);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        setIsPrinting(false);
      }, 500);
    };
  };

  const getReportStyles = () => `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; background: white; padding: 20px; }
    .report-container { max-width: 1200px; margin: 0 auto; background: white; }
    .report-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 3px solid #333; margin-bottom: 30px; }
    .logo-left, .logo-right { width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; }
    .company-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
    .logo-placeholder { width: 80px; height: 80px; background: #4caf50; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: white; border-radius: 50%; }
    .company-title { text-align: center; flex: 1; }
    .company-title h1 { font-size: 28px; color: #333; margin-bottom: 5px; font-weight: bold; }
    .acronym { font-size: 14px; color: #666; margin-bottom: 5px; }
    .location { font-size: 12px; color: #888; }
    .section { margin-bottom: 30px; page-break-inside: avoid; }
    .section h2 { font-size: 18px; color: #333; padding: 8px 12px; background: #f5f5f5; border-left: 4px solid #4caf50; margin-bottom: 15px; }
    .data-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    .data-table th, .data-table td { border: 1px solid #ddd; padding: 10px 12px; text-align: left; }
    .data-table th { background: #f8f9fa; font-weight: 600; font-size: 14px; }
    .data-table td { font-size: 13px; }
    .photo-cell { text-align: center; width: 100px; }
    .customer-photo { width: 80px; height: 80px; object-fit: cover; border-radius: 8px; border: 1px solid #ddd; }
    .no-photo { color: #999; font-size: 12px; }
    .signature-cell { font-family: monospace; font-size: 14px; letter-spacing: 1px; }
    .footer-message { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; }
    .footer-message p { font-size: 14px; color: #666; font-style: italic; }
    @media print { body { padding: 0; margin: 0; } .report-container { margin: 0; padding: 20px; } .section { page-break-inside: avoid; } .customer-photo { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;

  if (loading) {
    return (
      <div className="contract-report-loading">
        <div className="spinner"></div>
        <p>Loading contract details...</p>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="contract-report-error">
        <h3>Contract Not Found</h3>
        <p>The contract you're looking for doesn't exist.</p>
        <button onClick={() => navigate("/ViewContract")} className="back-btn">
          Back to Contracts
        </button>
      </div>
    );
  }

  const jobNo = getJobNumber();
  const jobTitle = getJobTitle();
  const jobDescription = getJobDescription();
  const shift = getShift();
  const photoUrl = getPhotoUrl();
  const companyLogoUrl = getCompanyLogoUrl();

  return (
    <div className="contract-report-wrapper">
      <div className="contract-report-header">
        <button onClick={() => navigate("/ViewContract")} className="back-button">
          <ArrowLeft size={20} /> Back to Contracts
        </button>
        <div className="header-actions">
          <button onClick={handlePrint} className="print-button" disabled={isPrinting}>
            <Printer size={18} /> {isPrinting ? "Printing..." : "Print Report"}
          </button>
        </div>
      </div>

      <div className="contract-report-preview">
        <div className="report-header">
          <div className="logo-left">
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt="Company Logo" className="company-logo" />
            ) : (
              <div className="logo-placeholder">SOMIS</div>
            )}
          </div>
          <div className="company-title">
            <h1>SOMIS COMPANY</h1>
            <p className="acronym">SOMIS</p>
            <p className="location">{company?.companylocation || "Mogadishu, Somalia"}</p>
          </div>
          <div className="logo-right">
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt="Company Logo" className="company-logo" />
            ) : (
              <div className="logo-placeholder">SOMIS</div>
            )}
          </div>
        </div>

        <div className="section">
          <h2>Job Details</h2>
          <table className="data-table">
            <thead>
              <tr><th>Job No</th><th>Job Title</th><th>Job Description</th></tr>
            </thead>
            <tbody>
              <tr><td>{jobNo}</td><td>{jobTitle}</td><td>{jobDescription}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="section">
          <h2>Personal Detail</h2>
          <table className="data-table">
            <thead>
              <tr><th>ID No</th><th>Full name</th><th>Photos</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>{contract.cust_id?.customer_id_no || contract.no || "N/A"}</td>
                <td>{contract.cust_id?.fullname || "N/A"}</td>
                <td className="photo-cell">
                  {photoUrl ? (
                    <img src={photoUrl} alt="Customer" className="customer-photo" />
                  ) : (
                    <span className="no-photo">No Image</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="section">
          <h2>Client Contract</h2>
          <table className="data-table">
            <thead>
              <tr><th>No</th><th>Client</th><th>Shift</th></tr>
            </thead>
            <tbody>
              <tr><td>{contract.no || "N/A"}</td><td>{contract.client_id?.name || "N/A"}</td><td>{shift}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="section">
          <h2>Contract Start and End</h2>
          <table className="data-table">
            <thead>
              <tr><th>No</th><th>Start</th><th>End</th></tr>
            </thead>
            <tbody>
              <tr><td>{contract.no || "N/A"}</td><td>{formatDate(contract.contract_start)}</td><td>{formatDate(contract.contract_end)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="section">
          <h2>Manager / Customer</h2>
          <table className="data-table">
            <thead>
              <tr><th>No</th><th>Name</th><th>Signature</th></tr>
            </thead>
            <tbody>
              <tr><td>1</td><td>{contract.cust_id?.fullname || "N/A"}</td><td className="signature-cell">___________________________</td></tr>
              <tr><td>2</td><td>{contract.client_id?.manager_name || "N/A"}</td><td className="signature-cell">___________________________</td></tr>
            </tbody>
          </table>
        </div>

        <div className="footer-message">
          <p>Good luck</p>
        </div>
      </div>
    </div>
  );
};

export default ContractReport;